import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Header from '@components/header/Header'
import ManifestationHeader from './ManifestationHeader'
import ManifestationTopSection from './ManifestationTopSection'
import IncidentListSection from '@components/sections/IncidentListSection'

import api from '@api/api'

import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'
import * as FiltreStore from '@store/filtre/filtre'
import { emptyFilters } from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { manifestationDate, manifestationStatus } from '../manifestationsUtils'
import { getNextActionOnManifestationDate } from '@utils/userUtils'
import { differenceInCalendarDays, isBefore } from 'date-fns'
import { AuthScope } from '@api/enum'

const MAIN_VERTICAL_PADDING = 24

interface Props {
  manifestationId: string
}

const ManifestationDetailScreen = ({ manifestationId }: Props) => {
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'
  const i18n = useI18n()

  const now = Date.now()

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const user = useReducer(UserStore.store, (s) => s.user)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const navigation = useNavigation()

  const [status, setStatus] = React.useState<Status>('loading')
  const [manifestation, setManifestation] = React.useState<ManifestationDetailResponse>()
  const [edlList, setEdlList] = React.useState<EtatDesLieuxResponse[]>([])
  const [hasScrolled, setHasScrolled] = React.useState(false)
  const [incidents, setIncidents] = React.useState<IncidentResponse[]>([])
  const [controles, setControles] = React.useState<ControleQualiteResponse[]>([])

  const started = !!manifestation && isBefore(new Date(manifestation.dateOuverturePublic), now)

  const manifStatus = !!manifestation && manifestationStatus(manifestation)

  const canStartIncident = manifStatus === 'inProgress' && !!manifestation && manifestation.actif
  const nextActionOnManifestationDate =
    !!manifestation && !!user ? getNextActionOnManifestationDate(user, manifestation) : undefined

  React.useEffect(() => {
    Promise.all([
      api.manifestation.get(site.id, manifestationId),
      api.incident.search({ manifestationIds: [manifestationId] }),
      scopes.includes(AuthScope.EdlSiteLecture) ? api.edl.all(site.id, manifestationId) : Promise.resolve([]),
      scopes.includes(AuthScope.ControleQualiteSiteLecture)
        ? api.controle.all({ manifestationIds: [manifestationId] })
        : Promise.resolve([]),
    ])
      .then(([manif, incs, edlList, controleList]) => {
        setManifestation(manif)
        setIncidents(incs)
        setEdlList(edlList)
        setControles(controleList)
        setStatus('fetched')
      })
      .catch(() => setStatus('error'))
  }, [manifestationId])

  const startButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push(`/incident/start?manifestationId=${manifestationId}`),
  }

  const onMapPressed = () => {
    if (manifestation) {
      FiltreStore.actions.setIncidentFilter({
        ...emptyFilters,
        manifestation: [manifestation.id],
        location: manifestation.espaceIds,
      })
    }
    navigation.push('/plan')
  }

  const headerButtons: HeaderButton[] = React.useMemo(() => {
    if (!manifestation) {
      return []
    }
    const map: HeaderButton[] = true // TODO : conditionner à la présence d'une map
      ? [
          {
            label: i18n.t('screens.site.plan'),
            onClick: onMapPressed,
            color: 'light',
          },
        ]
      : []

    const start = canStartIncident ? [startButton] : []
    return [...map, ...start]
  }, [manifestation, i18n, canStartIncident])

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t('screens.manifestations.title'), onClick: () => navigation.push('/manifestations') },
  ]

  const countdown = manifestation
    ? differenceInCalendarDays(new Date(manifestation.dateOuverturePublic), new Date())
    : 0

  const renderBottomButton =
    isPhone && canStartIncident
      ? () => (
          <BottomButton>
            <Button status="active" label={startButton.label} onClick={startButton.onClick} />
          </BottomButton>
        )
      : undefined

  const renderBottomHeader = () =>
    !!manifestation && <ManifestationDate>{manifestationDate(manifestation, i18n)}</ManifestationDate>

  return (
    <Root
      loading={status === 'loading'}
      renderFixedBottom={renderBottomButton}
      header={i18n.t('screens.manifestation.title')}
      fixed
      renderHeader={
        isPhone && !!manifestation
          ? () => (
              <ManifestationHeader
                edlList={edlList}
                controles={controles}
                hasScrolled={hasScrolled}
                manifestation={manifestation}
                onBack={navigation.goBack}
                onMapPressed={onMapPressed}
                nextActionOnManifestationDate={nextActionOnManifestationDate}
              />
            )
          : undefined
      }
      onLeft={navigation.goBack}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => setHasScrolled(event.nativeEvent.contentOffset.y > 0)}
        scrollEventThrottle={16}>
        {!!manifestation && status === 'fetched' ? (
          <>
            {!isPhone && (
              <Header
                links={headerLinks}
                title={manifestation.libelle}
                buttons={headerButtons}
                renderBottom={renderBottomHeader}
              />
            )}
            <MainContainer>
              {!isPhone && (
                <ManifestationTopSection manifestation={manifestation} edlList={edlList} controles={controles} />
              )}

              {!started && <Countdown>{i18n.t('screens.manifestation.countdown', { count: countdown })}</Countdown>}

              <IncidentListSection
                incidents={incidents}
                onSeeAll={() => {
                  FiltreStore.actions.setIncidentFilter({ ...emptyFilters, manifestation: [manifestation.id] })
                  navigation.push('/incidents')
                }}
              />
            </MainContainer>
          </>
        ) : (
          <MainContainer>
            <MissingInfo>{i18n.t('common.error')}</MissingInfo>
          </MainContainer>
        )}
      </ScrollView>
    </Root>
  )
}

export default ManifestationDetailScreen

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
const BottomButton = styled(View)`
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`
const ManifestationDate = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 8px;
`
const Countdown = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 16px;
`
